import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS,
  DEFAULT_DEPLOY_REFRESH_SAFE_TO_RELOAD_EVENT,
  fetchDeployRefreshBuildMeta,
  hasDifferentDeployRefreshBuildId,
  normalizeDeployRefreshBuildId,
  type DeployRefreshBuildMeta,
} from './meta';

export type DeployRefreshGuardClassNames = {
  banner?: string;
  copy?: string;
  title?: string;
  body?: string;
  action?: string;
};

export type DeployRefreshGuardMessages = {
  title: string;
  body: string;
  actionLabel: string;
};

export type DeployRefreshPromptRenderProps = {
  classNames?: DeployRefreshGuardClassNames;
  messages: DeployRefreshGuardMessages;
  onRefresh: () => void;
  staleMeta: DeployRefreshBuildMeta;
  testId: string;
};

export type DeployRefreshGuardProps = {
  currentBuildId: string;
  enabled?: boolean;
  fetchBuildMeta?: () => Promise<DeployRefreshBuildMeta | null>;
  isAutoReloadRoute?: (pathname: string) => boolean;
  pathname: string;
  pollIntervalMs?: number;
  reloadWindow?: () => void;
  safeToReloadEventName?: string;
  classNames?: DeployRefreshGuardClassNames;
  messages?: Partial<DeployRefreshGuardMessages>;
  renderPrompt?: (props: DeployRefreshPromptRenderProps) => ReactNode;
  testId?: string;
  logger?: Pick<Console, 'warn'>;
};

const defaultMessages: DeployRefreshGuardMessages = {
  title: 'A new version is available.',
  body: 'Save your work first, then refresh when ready.',
  actionLabel: 'Refresh now',
};

const neverAutoReloadRoute = () => false;

const reloadCurrentWindow = () => {
  if (typeof window === 'undefined') return;
  window.location.reload();
};

const renderDefaultPrompt = ({
  classNames,
  messages,
  onRefresh,
  testId,
}: DeployRefreshPromptRenderProps) => (
  <aside
    className={classNames?.banner}
    data-testid={testId}
    role="status"
    aria-live="polite"
  >
    <div className={classNames?.copy}>
      <p className={classNames?.title}>{messages.title}</p>
      <p className={classNames?.body}>{messages.body}</p>
    </div>
    <button
      className={classNames?.action}
      type="button"
      onClick={onRefresh}
    >
      {messages.actionLabel}
    </button>
  </aside>
);

export const DeployRefreshGuard = ({
  currentBuildId,
  enabled = true,
  fetchBuildMeta = fetchDeployRefreshBuildMeta,
  isAutoReloadRoute = neverAutoReloadRoute,
  pathname,
  pollIntervalMs = DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS,
  reloadWindow = reloadCurrentWindow,
  safeToReloadEventName = DEFAULT_DEPLOY_REFRESH_SAFE_TO_RELOAD_EVENT,
  classNames,
  messages,
  renderPrompt = renderDefaultPrompt,
  testId = 'deploy-refresh-guard-banner',
  logger = console,
}: DeployRefreshGuardProps) => {
  const resolvedMessages = { ...defaultMessages, ...messages };
  const [staleMeta, setStaleMeta] = useState<DeployRefreshBuildMeta | null>(null);
  const hasReloadedRef = useRef(false);
  const currentBuildIdRef = useRef(currentBuildId);
  currentBuildIdRef.current = currentBuildId;

  const reloadOnce = useCallback(() => {
    if (hasReloadedRef.current) return;
    hasReloadedRef.current = true;
    reloadWindow();
  }, [reloadWindow]);

  useEffect(() => {
    if (!enabled || !normalizeDeployRefreshBuildId(currentBuildId)) {
      setStaleMeta(null);
      return undefined;
    }

    let cancelled = false;
    const checkForNewBuild = async () => {
      try {
        const nextMeta = await fetchBuildMeta();
        if (cancelled) return;
        if (hasDifferentDeployRefreshBuildId(nextMeta, currentBuildIdRef.current)) {
          setStaleMeta(nextMeta);
        }
      } catch (error) {
        logger.warn('[DeployRefreshGuard] Unable to check deployed build metadata.', error);
      }
    };

    void checkForNewBuild();
    const intervalId = window.setInterval(
      () => {
        void checkForNewBuild();
      },
      Math.max(1_000, pollIntervalMs),
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentBuildId, enabled, fetchBuildMeta, logger, pollIntervalMs]);

  useEffect(() => {
    if (!staleMeta) return;
    if (isAutoReloadRoute(pathname)) {
      reloadOnce();
    }
  }, [isAutoReloadRoute, pathname, reloadOnce, staleMeta]);

  useEffect(() => {
    if (!staleMeta || typeof window === 'undefined') return undefined;
    const handleSafeToReload = () => reloadOnce();
    window.addEventListener(safeToReloadEventName, handleSafeToReload);
    return () => {
      window.removeEventListener(safeToReloadEventName, handleSafeToReload);
    };
  }, [reloadOnce, safeToReloadEventName, staleMeta]);

  if (!enabled || !staleMeta || isAutoReloadRoute(pathname)) {
    return null;
  }

  return renderPrompt({
    classNames,
    messages: resolvedMessages,
    onRefresh: reloadOnce,
    staleMeta,
    testId,
  });
};

export {
  DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS,
  DEFAULT_DEPLOY_REFRESH_SAFE_TO_RELOAD_EVENT,
  DEFAULT_DEPLOY_REFRESH_STORAGE_KEY,
  DEPLOY_REFRESH_META_PATH,
  createDeployRefreshBuildMeta,
  fetchDeployRefreshBuildMeta,
  hasDifferentDeployRefreshBuildId,
  isDeployRefreshGuardEnabled,
  normalizeDeployRefreshBuildId,
  parseDeployRefreshBuildMeta,
  readDeployRefreshStorageOverride,
  renderDeployRefreshBuildMetaJson,
} from './meta';
export type {
  CreateDeployRefreshBuildMetaInput,
  DeployRefreshBuildMeta,
  DeployRefreshGuardEnabledOptions,
  FetchDeployRefreshBuildMetaOptions,
} from './meta';
